// src/components/AdLogsSection.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAppContext } from '../../App';
import { api } from '../../apiService';

function AdLogsSection() {
    const { currentStationId, allStations } = useAppContext();
    const [campaigns, setCampaigns] = useState([]);
    const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [startTime, setStartTime] = useState('08:00');
    const [endTime, setEndTime] = useState('23:59');
    const [showAllCampaigns, setShowAllCampaigns] = useState(false);
    
    const urduNoteRef = useRef(null);

    useEffect(() => {
        if (!currentStationId || currentStationId === 'all') { 
            setCampaigns([]); 
            setLoading(false); 
            return; 
        }
        setLoading(true);
        const fetchCampaigns = async () => {
            try {
                const data = await api.get('ad_campaigns', { where: { stationId: currentStationId } });
                const normalized = (data || []).map(c => {
                    let subCampaigns = c.subCampaigns;
                    if (typeof subCampaigns === 'string') {
                        try { subCampaigns = JSON.parse(subCampaigns); } catch { subCampaigns = []; }
                    }
                    return { ...c, subCampaigns: Array.isArray(subCampaigns) ? subCampaigns : [] };
                });
                setCampaigns(normalized);
            } catch (err) {
                setError(`Error loading campaigns: ${err.message}`); 
                console.error(err); 
            } finally {
                setLoading(false);
            }
        };
        fetchCampaigns();
    }, [currentStationId]);

    const generateTimeSlots = (start, end) => {
        const slots = [];
        const [startHour, startMinute] = start.split(':').map(Number);
        const [endHour, endMinute] = end.split(':').map(Number);

        for (let h = startHour; h <= endHour; h++) {
            const mStart = (h === startHour) ? Math.ceil(startMinute / 15) * 15 : 0;
            const mEnd = (h === endHour) ? endMinute : 59;
            for (let m = mStart; m <= mEnd; m += 15) {
                if (h === endHour && m > endMinute) continue;
                const hour = h.toString().padStart(2, '0');
                const minute = m.toString().padStart(2, '0');
                slots.push(`${hour}:${minute}`);
            }
        }
        return slots;
    };

    const filteredAndGroupedCampaigns = useMemo(() => {
        const selectedDateStr = (filterDate || '').slice(0, 10);
        const relevantCampaigns = campaigns.filter(campaign => {
            if (showAllCampaigns) return true;
            const start = String(campaign.startDate || '').slice(0, 10);
            const end = String(campaign.endDate || '').slice(0, 10);
            if (!start || !end || !selectedDateStr) return false;
            return selectedDateStr >= start && selectedDateStr <= end;
        });

        const timeSlots = generateTimeSlots(startTime, endTime);
        const groupedByTime = {};
        timeSlots.forEach(slot => { groupedByTime[slot] = []; });

        relevantCampaigns.forEach(campaign => {
            if (Array.isArray(campaign.subCampaigns)) {
                campaign.subCampaigns.forEach(part => {
                    if (part.name && Array.isArray(part.timings)) {
                        part.timings.forEach(timing => {
                            if (!timing) return;
                            const [hour, minute] = timing.split(':').map(Number);
                            const slotMinute = Math.floor(minute / 15) * 15;
                            const slotKey = `${hour.toString().padStart(2, '0')}:${slotMinute.toString().padStart(2, '0')}`;
                            if (groupedByTime[slotKey] && !groupedByTime[slotKey].includes(part.name)) {
                                groupedByTime[slotKey].push(part.name);
                            }
                        });
                    }
                });
            }
        });
        return groupedByTime;
    }, [campaigns, filterDate, startTime, endTime, showAllCampaigns]);
    
    const displayDate = new Date(filterDate  + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const currentStationName = !currentStationId || currentStationId === 'all'
        ? "Select a Station" 
        : (allStations.find(s => s.id === currentStationId)?.name || '...');

    const handleExportPDF = () => { /* PDF export functionality can be added here if needed */ };

    return (
        <div className="container mx-auto">
             <div className="mb-8 flex justify-between items-center">
                <div>
                    <h1 className="text-4xl font-bold text-gray-800">Ad Broadcast Log</h1>
                    <p className="text-gray-500 mt-1">View the ad schedule for any given day and time range.</p>
                </div>
                <button onClick={handleExportPDF} disabled={currentStationId === 'all'} className="bg-green-600 text-white font-bold py-2 px-5 rounded-lg shadow-md hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                    <span>Export as PDF</span>
                </button>
             </div>

            <div className="bg-white p-6 rounded-2xl shadow-lg mb-8">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <div> <label className="block text-sm font-medium text-gray-600">Date</label> <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} className="mt-1 block w-full bg-gray-50 border-gray-300 rounded-md shadow-sm p-3 font-semibold"/> </div>
                    <div> <label className="block text-sm font-medium text-gray-600">Start Time</label> <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className="mt-1 block w-full bg-gray-50 border-gray-300 rounded-md shadow-sm p-3 font-semibold"/> </div>
                    <div> <label className="block text-sm font-medium text-gray-600">End Time</label> <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className="mt-1 block w-full bg-gray-50 border-gray-300 rounded-md shadow-sm p-3 font-semibold"/> </div>
                    <div className="text-right"> <p className="font-bold text-lg text-rose-600">{currentStationName}</p> <p className="text-sm text-gray-500">{displayDate}</p> </div>
                </div>
                <div className="mt-4 flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                        <input type="checkbox" checked={showAllCampaigns} onChange={(e) => setShowAllCampaigns(e.target.checked)} className="h-4 w-4 text-rose-600 border-gray-300 rounded focus:ring-rose-500" />
                        Show all campaigns (ignore date range)
                    </label>
                    <div className="text-sm text-gray-500 font-semibold">Campaigns loaded: {campaigns.length}</div>
                </div>
            </div>
            
            <div ref={urduNoteRef} dir="rtl" className="bg-rose-100 border-r-4 border-rose-500 text-rose-900 p-4 mb-8 font-[Jameel_Noori_Nastaleeq,Arial,sans-serif]" style={{textAlign: "right"}}>
                <p className="font-bold text-xl">ریڈیو ٹیم کے لیے اہم ہدایت</p>
                <p className="text-lg mt-1">کمپیوٹر آپریٹر سختی سے صرف اس لاگ میں درج اشتہارات چلانے کا پابند ہے۔ کسی بھی غیر منظور شدہ اشتہار کو چلانے کی ممانعت ہے۔ اس شیڈول سے انحراف کی صورت میں آپریٹر خود ذمہ دار ہوگا۔</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8">
                {!currentStationId || currentStationId === 'all' ? ( <div className="text-center p-10"><h3 className="text-xl font-bold text-gray-700">Please select a station to view the ad log.</h3></div> ) 
                : loading ? ( <p className="text-center py-8">Loading ad campaigns...</p> ) 
                : error ? ( <div className="p-4 bg-red-100 text-red-700 rounded-md font-bold">{error}</div> ) 
                : (
                    <div className="space-y-8">
                        {Object.keys(filteredAndGroupedCampaigns).length === 0 ? (<p className="text-center text-gray-500 py-8">No ads scheduled for this period.</p>) 
                        : Object.keys(filteredAndGroupedCampaigns).map((time) => {
                            const ads = filteredAndGroupedCampaigns[time];
                            const hour = time.split(':')[0];
                            const isNewHour = time.endsWith(':00');
                            
                            return (
                                <div key={time}>
                                    {isNewHour && (<div className="flex items-center gap-4 my-6"><div className="flex-grow border-t border-gray-200"></div><h2 className="text-xl font-bold text-gray-700 bg-gray-100 px-4 py-1 rounded-full">{hour}:00 Hour</h2><div className="flex-grow border-t border-gray-200"></div></div>)}
                                    <div className="flex items-start gap-4">
                                        <div className="flex flex-col items-center"><div className="w-24 text-right font-bold text-rose-600 pr-4">{time}</div></div>
                                        <div className="relative w-full">
                                            <div className="absolute left-0 top-2 h-full w-0.5 bg-gray-200 -translate-x-4"></div><div className="absolute left-0 top-2 w-4 h-4 bg-white border-2 border-rose-500 rounded-full -translate-x-[22px]"></div>
                                            <div className={`p-4 rounded-lg ${ads.length > 0 ? 'bg-rose-50 border border-rose-100' : 'bg-gray-50 border border-gray-100'}`}>
                                                <div className="flex flex-wrap gap-2">
                                                    {ads.map((ad, adIndex) => (
                                                        <span key={adIndex} className="h-7 inline-flex items-center justify-center bg-rose-600 text-white text-sm font-semibold px-4 rounded-full shadow">{ad}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

export default AdLogsSection;
